import React from 'react';
import { motion } from 'framer-motion';
import { Container } from './Container';
import { AppRoutes } from '../../types';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { usePageVisibility } from '../../hooks/usePageVisibility';
import { fadeInUpFast as fadeInUp, staggerContainer } from '../../lib/animation';
import { NewsletterSignup } from './NewsletterSignup';

export const Footer: React.FC = () => {
  const { content, makePath, language } = useLanguage();
  const { isVisible } = usePageVisibility();
  const c = content.footer;
  const n = content.nav;

  const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL;
  const logoUrl = supabaseUrl
    ? `${supabaseUrl}/storage/v1/object/public/natuvia-assets/brand/logo-footer.svg`
    : '/brand/logo-footer.svg';

  const footerLabels = {
    en: {
      support: 'Support',
      contact: 'Contact Us',
      shipping: 'Shipping & Returns',
      privacy: 'Privacy Policy',
      terms: 'Terms & Conditions',
    },
    'zh-hk': {
      support: '客戶服務',
      contact: '聯絡我們',
      shipping: '運送與退換',
      privacy: '私隱政策',
      terms: '條款及細則',
    },
  };
  const labels = footerLabels[language as keyof typeof footerLabels] || footerLabels['en'];

  return (
    <footer className="bg-natuvia-olive text-natuvia-pinkish mt-auto relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-white/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-72 h-72 bg-natuvia-cta/5 rounded-full blur-3xl translate-x-1/3 translate-y-1/3" />

      <Container className="py-16 md:py-20 relative z-10">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
          variants={staggerContainer}
          className="grid grid-cols-2 md:grid-cols-5 gap-10 md:gap-12"
        >
          {/* Brand Column */}
          <motion.div variants={fadeInUp} className="col-span-2 space-y-6">
            <Link to={makePath(AppRoutes.HOME)} className="inline-block group">
              <div className="flex items-center">
                <img
                  src={logoUrl}
                  alt="Natuvía"
                  width={100}
                  height={56}
                  loading="lazy"
                  decoding="async"
                  className="h-12 md:h-14 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                  onError={e => {
                    e.currentTarget.style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'block';
                  }}
                />
                <span className="hidden text-3xl font-extrabold tracking-tight text-natuvia-pinkish">
                  Natuvía
                </span>
              </div>
            </Link>
            <p className="max-w-md opacity-80 whitespace-pre-line leading-relaxed text-sm">
              {c.brandDesc}
            </p>

            <div className="max-w-md">
              <NewsletterSignup />
            </div>

            {/* Social Icons - moved here for better visibility */}
            <div className="flex gap-3 pt-2">
              <a
                href="https://www.instagram.com/NatuviaPet/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-natuvia-cta hover:scale-110 transition-all duration-300"
                aria-label="Instagram"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>
              <a
                href="https://www.facebook.com/NatuviaPet"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-natuvia-cta hover:scale-110 transition-all duration-300"
                aria-label="Facebook"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
            </div>
          </motion.div>

          {/* Explore Column */}
          <motion.div variants={fadeInUp} className="space-y-5">
            <h3 className="font-bold text-white text-lg tracking-wide">{c.explore}</h3>
            <ul className="space-y-3 text-sm">
              {isVisible('about') && (
                <li>
                  <Link
                    to={makePath(AppRoutes.ABOUT)}
                    className="opacity-70 hover:opacity-100 hover:text-natuvia-cta transition-all duration-300 inline-flex items-center gap-1 group"
                  >
                    <span className="group-hover:translate-x-1 transition-transform">
                      {n.about}
                    </span>
                  </Link>
                </li>
              )}
              {isVisible('products') && (
                <li>
                  <Link
                    to={makePath(AppRoutes.PRODUCTS)}
                    className="opacity-70 hover:opacity-100 hover:text-natuvia-cta transition-all duration-300 inline-flex items-center gap-1 group"
                  >
                    <span className="group-hover:translate-x-1 transition-transform">
                      {n.products}
                    </span>
                  </Link>
                </li>
              )}
              {isVisible('journal') && (
                <li>
                  <Link
                    to={makePath(AppRoutes.JOURNAL)}
                    className="opacity-70 hover:opacity-100 hover:text-natuvia-cta transition-all duration-300 inline-flex items-center gap-1 group"
                  >
                    <span className="group-hover:translate-x-1 transition-transform">
                      {n.journal}
                    </span>
                  </Link>
                </li>
              )}
              {isVisible('faq') && (
                <li>
                  <Link
                    to={makePath(AppRoutes.FAQ)}
                    className="opacity-70 hover:opacity-100 hover:text-natuvia-cta transition-all duration-300 inline-flex items-center gap-1 group"
                  >
                    <span className="group-hover:translate-x-1 transition-transform">{n.faq}</span>
                  </Link>
                </li>
              )}
              {isVisible('petServices') && (
                <li>
                  <Link
                    to={makePath(AppRoutes.PET_SERVICES)}
                    className="opacity-70 hover:opacity-100 hover:text-natuvia-cta transition-all duration-300 inline-flex items-center gap-1 group"
                  >
                    <span className="group-hover:translate-x-1 transition-transform">
                      {n.petServices}
                    </span>
                  </Link>
                </li>
              )}
            </ul>
          </motion.div>

          {/* Support Column */}
          <motion.div variants={fadeInUp} className="space-y-5">
            <h3 className="font-bold text-white text-lg tracking-wide">{labels.support}</h3>
            <ul className="space-y-3 text-sm">
              {isVisible('contact') && (
                <li>
                  <Link
                    to={makePath(AppRoutes.CONTACT)}
                    className="opacity-70 hover:opacity-100 hover:text-natuvia-cta transition-all duration-300 inline-flex items-center gap-1 group"
                  >
                    <span className="group-hover:translate-x-1 transition-transform">
                      {labels.contact}
                    </span>
                  </Link>
                </li>
              )}
              {isVisible('shipping') && (
                <li>
                  <Link
                    to={makePath(AppRoutes.SHIPPING)}
                    className="opacity-70 hover:opacity-100 hover:text-natuvia-cta transition-all duration-300 inline-flex items-center gap-1 group"
                  >
                    <span className="group-hover:translate-x-1 transition-transform">
                      {labels.shipping}
                    </span>
                  </Link>
                </li>
              )}
              {isVisible('privacy') && (
                <li>
                  <Link
                    to={makePath(AppRoutes.PRIVACY)}
                    className="opacity-70 hover:opacity-100 hover:text-natuvia-cta transition-all duration-300 inline-flex items-center gap-1 group"
                  >
                    <span className="group-hover:translate-x-1 transition-transform">
                      {labels.privacy}
                    </span>
                  </Link>
                </li>
              )}
              {isVisible('terms') && (
                <li>
                  <Link
                    to={makePath(AppRoutes.TERMS)}
                    className="opacity-70 hover:opacity-100 hover:text-natuvia-cta transition-all duration-300 inline-flex items-center gap-1 group"
                  >
                    <span className="group-hover:translate-x-1 transition-transform">
                      {labels.terms}
                    </span>
                  </Link>
                </li>
              )}
            </ul>
          </motion.div>

          {/* Connect Column */}
          <motion.div variants={fadeInUp} className="space-y-5">
            <h3 className="font-bold text-white text-lg tracking-wide">{c.connect}</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  href="https://www.instagram.com/NatuviaPet/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="opacity-70 hover:opacity-100 hover:text-natuvia-cta transition-all duration-300 inline-flex items-center gap-2 group"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                  <span className="group-hover:translate-x-1 transition-transform">Instagram</span>
                </a>
              </li>
              <li>
                <a
                  href="https://www.facebook.com/NatuviaPet"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="opacity-70 hover:opacity-100 hover:text-natuvia-cta transition-all duration-300 inline-flex items-center gap-2 group"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                  <span className="group-hover:translate-x-1 transition-transform">Facebook</span>
                </a>
              </li>
              <li className="pt-3">
                <span className="opacity-50 text-xs tracking-wider">@NatuviaPet</span>
              </li>
            </ul>
          </motion.div>
        </motion.div>

        {/* Bottom bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="border-t border-white/10 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center text-sm"
        >
          <p className="opacity-50">&copy; 2024 {c.copyright}</p>
          <div className="mt-4 md:mt-0 flex items-center gap-4">
            <span className="opacity-50 font-medium">www.natuvia.com</span>
            <span className="hidden md:inline opacity-30">|</span>
            <span className="hidden md:inline opacity-40 text-xs">
              {language === 'en' ? 'Made with love for pets' : '用愛為寵物打造'}
            </span>
          </div>
        </motion.div>
      </Container>
    </footer>
  );
};
